<template>
  <div :class="[$style.navigationPadding]">
    <nav
      class="pagination is-small is-marginless"
      role="navigation"
      aria-label="pagination"
    >
      <div :class="[$style.customNavigation]" @submit.prevent>
        <span :class="$style.customPadding"> Page </span> &nbsp;
        <input
          :class="$style.pageInput"
          v-bind:value="page"
          v-on:input="insertedPageNumber = $event.target.value"
        />
        &nbsp;
        <span :class="$style.customPadding">
          of &nbsp; {{ totalNumberOfPage }}</span
        >
        <button
          :class="[$style.customNavigationButton]"
          @click="$emit('update:page', insertedPageNumber)"
        >
          Go
        </button>
      </div>

      <ul class="pagination-list">
        <li>
          <button
            class="pagination-previous"
            :class="$style.customButtonPadding"
            aria-label="page 1"
            :disabled="isFirstPage"
            @click="$emit('update:page', 1)"
          >
            First
          </button>
        </li>
        <li>
          <button
            class="pagination-previous"
            :class="$style.customButtonPadding"
            aria-label="Page 1"
            :disabled="isFirstPage"
            @click="$emit('update:page', page - 1)"
          >
            Prev
          </button>
        </li>

        <b-pagination
          data-test="paginator"
          class="is-marginless"
          :class="$style.pagination"
          :total="totalCount"
          :current="page"
          :per-page="perPage"
          size="is-small"
          @change="$emit('update:page', $event)"
          aria-next-label="Next page"
          aria-previous-label="Previous page"
          aria-page-label="Page"
          aria-current-label="Current page"
        ></b-pagination>

        <li>
          <button
            :class="['pagination-next', $style.customButtonPadding]"
            aria-label="Next"
            :disabled="isLastPage"
            @click="$emit('update:page', page + 1)"
          >
            Next
          </button>
        </li>
        <li>
          <button
            :class="['pagination-next', $style.customButtonPadding]"
            aria-label="Last"
            :disabled="isLastPage"
            @click="$emit('update:page', totalNumberOfPage)"
          >
            Last
          </button>
        </li>
      </ul>
    </nav>

    <div :class="[$style.customNavigationItem]">
      <span
        :class="[$style.customNavigationItemList]"
        :style="{
          'border-right': showPerPageOptions ? '1px solid #dfdfdf' : 'none',
        }"
      >
        Results showing item {{ startItemIndex }} - {{ endItemIndex }} from
        {{ totalCount }} items
      </span>
      <span
        :class="[$style.customNavigationItemListPerPage]"
        v-if="showPerPageOptions"
      >
        list per page
        <b-select
          :value="perPage"
          @input="$emit('update:perPage', $event)"
          size="is-small"
          :class="[$style.perPageSelect, 'is-inline-block m-l-xs']"
        >
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="200">200</option>
        </b-select>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { uniq, cloneDeep as _cloneDeep } from 'lodash';

@Component({})
export default class BasePagination extends Vue {
  @Prop(Number) public page!: number;
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Number) public perPage!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop({ type: Boolean, default: true })
  public showPerPageOptions!: boolean;

  public insertedPageNumber: number = _cloneDeep(this.page);
}
</script>

<style lang="scss" module>
@mixin font-size($size, $weight: 400) {
  font-size: $size;
  font-weight: $weight;
}

.pagination {
  :global .is-current {
    background-color: inherit;
    border-color: $primary;
    color: $primary;
  }

  :global {
    .pagination-previous,
    .pagination-next {
      display: none;
    }
  }
}

.navigationPadding {
  border-radius: 0 0 6px 6px;
  padding: 12px 0 12px;
}

.customNavigation {
  font-size: $size-5;
  order: 3;
  display: flex;
  flex-direction: row;

  .customPadding {
    margin-top: 6px;
    margin-right: 6px;
  }

  .pageInput {
    display: flex;
    background-color: white;
    border: 1px solid #dfdfdf;
    border-radius: 4px;
    width: 30px;
    height: 30px;
    color: #444f51;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    text-align: center;
    margin-right: 6px;
  }

  .customNavigationButton {
    width: 38px;
    height: 30px;
    border-radius: 4px;
    color: white;
    font-size: 12px;
    font-weight: 400;
    line-height: 14px;
    margin-left: 5px;
    background-color: $primary;
    border-color: transparent;
  }
}

.customNavigationInput {
  width: 1.5rem;
  font-size: $size-7;
}

.customActiveButton {
  &#{&} {
    background-color: inherit;
    border-color: $primary;
    color: $primary;
  }
}

.customButtonPadding {
  &#{&} {
    padding: 0 10px 0 10px;
    background-color: $grey-light-3;
  }
}

.customNavigationItem {
  padding-top: 8px;
}

.customNavigationItemList {
  // border-right: 1px solid $grey-border;
  padding-right: 10px;
  font-size: $size-6;
}

.customNavigationItemListPerPage {
  padding-left: 10px;
  font-size: $size-6;

  select {
    background-color: transparent;
    border: none;
    color: $blue-jbi;
    font-weight: $weight-semibold;
  }

  :global .select:not(.is-multiple):not(.is-loading)::after {
    border-color: $dark;
    margin-top: -5px;
  }
}
.perPageSelect {
  line-height: 1.5;
}
</style>
